import React, { useState, useEffect } from "react";
import Layout from "../../Layout";
import { Button, FromToDate, Select } from "../../components/Form";
import { Transactiontable } from "../../components/Tables";
import { sortsDatas } from "../../components/Datas";
import { BiChevronDown, BiTime } from "react-icons/bi";
import {
  MdFilterList,
  MdOutlineCalendarMonth,
  MdOutlineCloudDownload,
} from "react-icons/md";
import { toast } from "react-hot-toast";
import { BsCalendarMonth } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import paymentService from "../../api/paymentService";

function Payments() {
  const [status, setStatus] = useState(sortsDatas.status[0]);
  const [method, setMethod] = useState(sortsDatas.method[0]);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({
    todayTotal: 0,
    monthTotal: 0,
    yearTotal: 0,
  });

  const sorts = [
    {
      id: 2,
      selected: status,
      setSelected: setStatus,
      datas: sortsDatas.status,
    },
    {
      id: 3,
      selected: method,
      setSelected: setMethod,
      datas: sortsDatas.method,
    },
  ];

  // Fetch payment stats (today, monthly, yearly)
  const fetchStats = async () => {
    try {
      const res = await paymentService.getPaymentSummary();
      if (res.success) {
        setStats({
          todayTotal: res.todayTotal || 0,
          monthTotal: res.monthTotal || 0,
          yearTotal: res.yearTotal || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch payment stats:", error);
    }
  };

  const fetchPayments = async () => {
    try {
      const filters = {
        search,
        status: status.name !== "Status..." ? status.name : "",
        method: method.name !== "Payment method" ? method.name : "",
        startDate,
        endDate,
      };
      const res = await paymentService.getAllPayments(filters);
      if (res.success) {
        const mappedData = res.data.map((item) => ({
          ...item,
          id: item._id,
          user: item.patient
            ? {
                title: item.patient.fullName,
                image: item.patient.image || "/images/user.png",
                phone: item.patient.mobile,
              }
            : { title: "Unknown", phone: "N/A" },
          date: new Date(item.createdAt).toDateString(),
          amount: item.amount,
          status: item.status, // Paid, Pending
          method: "Cash", // Default or map if backend provides
        }));
        setPayments(mappedData);
      }
    } catch (error) {
      toast.error("Failed to fetch payments");
    }
  };

  useEffect(() => {
    fetchStats(); // Fetch stats on mount
  }, []);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method, dateRange, search]);

  // Format number with commas (Indian format)
  const formatAmount = (amount) => {
    return amount.toLocaleString("en-IN");
  };

  // boxes
  const boxes = [
    {
      id: 1,
      title: "Today Payments",
      value: formatAmount(stats.todayTotal),
      color: ["bg-subMain", "text-subMain"],
      icon: BiTime,
    },
    {
      id: 2,
      title: "Monthly Payments",
      value: formatAmount(stats.monthTotal),
      color: ["bg-orange-500", "text-orange-500"],
      icon: BsCalendarMonth,
    },
    {
      id: 3,
      title: "Yearly Payments",
      value: formatAmount(stats.yearTotal),
      color: ["bg-green-500", "text-green-500"],
      icon: MdOutlineCalendarMonth,
    },
  ];

  const editPayment = (id) => {
    navigate(`/payments/edit/${id}`);
  };
  // preview
  const previewPayment = (id) => {
    navigate(`/payments/preview/${id}`);
  };

  const handleExport = async () => {
    try {
      const blob = await paymentService.exportPaymentsPdf();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Payments-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <Layout>
      {/* add button */}
      <button
        onClick={handleExport}
        className='flex-rows button-fb group fixed bottom-8 right-12 z-50 h-16 w-16 gap-4 rounded-full border border-border bg-[#0095D9] text-white transition-all duration-300 ease-in-out hover:h-14 hover:w-44'
      >
        <p className='hidden text-sm group-hover:block'>Export</p>
        <MdOutlineCloudDownload className='text-2xl' />
      </button>
      <h1 className='text-xl font-semibold'>Payments</h1>
      {/* boxes */}
      <div className='mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
        {boxes.map((box) => (
          <div
            key={box.id}
            className='flex-btn gap-4 rounded-xl border-[1px] border-border bg-white p-5'
          >
            <div className='w-3/4'>
              <h2 className='text-sm font-medium'>{box.title}</h2>
              <h2 className='my-6 text-xl font-medium'>{box.value}</h2>
              <p className='text-xs text-textGray'>
                You made <span className={box.color[1]}>{box.value}</span>{" "}
                transactions{" "}
                {box.title === "Today Payments"
                  ? "today"
                  : box.title === "Monthly Payments"
                    ? "this month"
                    : "this year"}
              </p>
            </div>
            <div
              className={`flex-colo text-md h-10 w-10 rounded-md text-white ${box.color[0]}`}
            >
              <box.icon />
            </div>
          </div>
        ))}
      </div>
      {/* datas */}
      <div
        data-aos='fade-up'
        data-aos-duration='1000'
        data-aos-delay='10'
        data-aos-offset='200'
        className='my-8 rounded-xl border-[1px] border-border bg-white p-5'
      >
        <div className='grid grid-cols-1 gap-2 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-5'>
          <input
            type='text'
            placeholder='Search "Patients"'
            className='h-14 rounded-md border border-border bg-dry px-4 text-sm text-main'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* sort  */}
          {sorts.map((item) => (
            <Select
              key={item.id}
              selectedPerson={item.selected}
              setSelectedPerson={item.setSelected}
              datas={item.datas}
            >
              <div className='flex h-14 w-full items-center justify-between rounded-md border border-border bg-dry px-4 text-xs text-main'>
                <p>{item.selected.name}</p>
                <BiChevronDown className='text-xl' />
              </div>
            </Select>
          ))}
          {/* date */}
          <FromToDate
            startDate={startDate}
            endDate={endDate}
            bg='bg-dry'
            onChange={(update) => setDateRange(update)}
          />
          {/* export */}
          <Button label='Filter' Icon={MdFilterList} onClick={fetchPayments} />
        </div>
        <div className='mt-8 w-full overflow-x-scroll'>
          <Transactiontable
            data={payments}
            action={true}
            functions={{
              edit: editPayment,
              preview: previewPayment,
            }}
          />
        </div>
      </div>
    </Layout>
  );
}

export default Payments;
